import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as Icons from 'lucide-react';
import { ArrowRight, Radio, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Seo } from '@/components/Seo';
import { Reveal, Stagger, StaggerItem } from '@/components/Reveal';
import { DeviceFrame, ProviderScreen } from '@/components/AppScreens';
import { CtaBanner } from '@/components/sections/CtaBanner';
import { PricingCards } from '@/components/sections/PricingCards';

const A = '#00FFA3';

export default function Providers() {
  const { t } = useTranslation();
  const steps = t('providersPage.steps', { returnObjects: true });
  const benefits = t('providersPage.benefits', { returnObjects: true });

  return (
    <>
      <Seo title={t('providersPage.seoTitle')} description={t('providersPage.seoDescription')} />

      <section className="relative overflow-hidden px-6 pb-16 pt-32 lg:px-8 sm:pt-40">
        <div className="animate-blob pointer-events-none absolute left-0 top-24 -z-10 h-96 w-96 rounded-full blur-[120px]" style={{ background: `${A}22` }} />
        <div className="mx-auto grid max-w-6xl items-center gap-14 lg:grid-cols-2">
          <div className="text-center lg:text-left">
            <span className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-widest" style={{ background: `${A}1a`, color: A }}>
              <Radio className="h-3.5 w-3.5" /> {t('providersPage.badge')}
            </span>
            <h1 className="mt-4 font-display text-4xl font-semibold leading-tight tracking-tight text-white sm:text-5xl lg:text-6xl">
              {t('providersPage.title1')} <span style={{ color: A, textShadow: `0 0 24px ${A}70` }}>{t('providersPage.title2')}</span>
            </h1>
            <p className="mx-auto mt-5 max-w-lg text-lg text-white/60 lg:mx-0">
              {t('providersPage.subtitle')}
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-4 lg:justify-start">
              <Link to="/pricing" className="inline-flex items-center justify-center gap-2 rounded-full px-7 py-3 font-semibold text-black transition-all hover:-translate-y-0.5" style={{ background: A, boxShadow: `0 0 25px ${A}55` }}>
                {t('common.startFreeTrial')} <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/followers" className="btn-secondary">{t('providersPage.ctaFollower')}</Link>
            </div>
          </div>
          <Reveal delay={0.15} className="perspective flex justify-center">
            <DeviceFrame tilt width="w-[280px]"><ProviderScreen /></DeviceFrame>
          </Reveal>
        </div>
      </section>

      {/* How sharing works */}
      <section className="px-6 py-24 sm:py-32 lg:px-8">
        <div className="mx-auto grid max-w-6xl gap-10 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-28">
              <span className="text-xs font-semibold uppercase tracking-[0.25em]" style={{ color: A }}>{t('providersPage.howEyebrow')}</span>
              <h2 className="mt-4 font-display text-3xl font-bold leading-[1.1] tracking-tight text-white sm:text-4xl lg:text-[2.75rem]">
                {t('providersPage.howTitle')}
              </h2>
              <p className="mt-5 max-w-md text-base leading-relaxed text-white/60">
                {t('providersPage.howSubtitle')}
              </p>
            </div>
          </div>
          <div className="lg:col-span-7">
            <div className="relative">
              <div className="absolute bottom-4 left-[19px] top-3 w-px" style={{ background: `linear-gradient(to bottom, ${A}80, ${A}33, transparent)` }} />
              {steps.map((s, i) => (
                <Reveal key={s.title} delay={i * 0.08}>
                  <div className="relative flex items-start gap-5 pb-5 last:pb-0">
                    <div className="relative z-10 flex h-10 w-10 shrink-0 items-center justify-center rounded-full font-display font-bold text-black ring-4 ring-[#00121D]" style={{ background: A, boxShadow: `0 0 20px ${A}66` }}>{i + 1}</div>
                    <div className="flex-1 rounded-2xl border border-white/10 bg-surface/70 p-5 transition-colors" style={{ borderColor: 'rgba(255,255,255,0.1)' }}>
                      <h3 className="font-display text-lg font-semibold text-white">{s.title}</h3>
                      <p className="mt-1 text-sm text-white/60">{s.desc}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why providers use Copy.Click */}
      <section className="px-6 py-24 sm:py-32 lg:px-8">
        <Reveal className="mx-auto max-w-6xl">
          <div className="max-w-2xl">
            <span className="text-xs font-semibold uppercase tracking-[0.25em]" style={{ color: A }}>{t('providersPage.whyEyebrow')}</span>
            <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-[2.75rem] lg:leading-[1.1]">{t('providersPage.whyTitle')}</h2>
          </div>
        </Reveal>
        <Stagger className="mx-auto mt-14 grid max-w-6xl gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {benefits.map((b) => {
            const Icon = Icons[b.icon] || Check;
            return (
              <StaggerItem key={b.title}>
                <motion.div whileHover={{ y: -6 }} className="group h-full rounded-2xl border border-white/10 bg-surface/70 p-7 transition-colors">
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#001826] transition-transform duration-300 group-hover:scale-110" style={{ color: A, boxShadow: `inset 0 0 0 1px ${A}33, 0 0 24px ${A}22` }}>
                    <Icon className="h-6 w-6" />
                  </div>
                  <h3 className="mt-5 font-display text-lg font-semibold text-white">{b.title}</h3>
                  <p className="mt-2 text-sm text-white/60">{b.desc}</p>
                </motion.div>
              </StaggerItem>
            );
          })}
        </Stagger>
      </section>

      <section className="px-6 py-24 sm:py-32 lg:px-8">
        <SectionHeadingLeft eyebrow={t('providersPage.pricingEyebrow')} title={t('providersPage.pricingTitle')} subtitle={t('providersPage.pricingSubtitle')} />
        <div className="mx-auto mt-12 max-w-6xl"><PricingCards audience="provider" /></div>
      </section>

      <CtaBanner />
    </>
  );
}

const SectionHeadingLeft = ({ eyebrow, title, subtitle }) => (
  <div className="mx-auto max-w-6xl">
    <div className="max-w-2xl">
      <span className="text-xs font-semibold uppercase tracking-[0.25em]" style={{ color: A }}>{eyebrow}</span>
      <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-[2.75rem] lg:leading-[1.1]">{title}</h2>
      <p className="mt-4 text-base leading-relaxed text-white/60">{subtitle}</p>
    </div>
  </div>
);
